import type { MacroStepV1, PlaybackMode } from "../types";

export const MAX_REPEAT = 100_000;
export const MAX_EXECUTED_STEPS = 1_000_000;

export interface StepResult {
  ok: boolean;
  exhausted?: boolean;
  cursor: number;
  targetEof: number;
}

export interface PlaybackAdapter {
  cursor(): number;
  docLength(): number;
  execute(step: MacroStepV1, targetEof: number): StepResult;
  beginHistoryGroup(): void;
  endHistoryGroup(): void;
  yieldControl(): Promise<void>;
}

export interface PlaybackResult { iterations: number; steps: number; reason: "complete" | "cancelled" | "eof" | "stalled" | "failed" | "limit"; }

export async function playMacro(
  macro: readonly MacroStepV1[], mode: PlaybackMode, adapter: PlaybackAdapter,
  signal: AbortSignal, onProgress?: (iterations: number, steps: number) => void
): Promise<PlaybackResult> {
  if (mode.kind === "count" && (!Number.isInteger(mode.iterations) || mode.iterations < 1 || mode.iterations > MAX_REPEAT)) {
    throw new Error(`Repeat count must be between 1 and ${MAX_REPEAT.toLocaleString()}.`);
  }
  if (macro.length === 0) return { iterations: 0, steps: 0, reason: "complete" };
  const maxIterations = mode.kind === "once" ? 1 : mode.kind === "count" ? mode.iterations : MAX_REPEAT;
  let targetEof = adapter.docLength();
  let executed = 0;
  let iterations = 0;
  let reason: PlaybackResult["reason"] = "complete";
  adapter.beginHistoryGroup();
  try {
    iteration: for (; iterations < maxIterations; iterations++) {
      const before = adapter.cursor();
      for (const step of macro) {
        if (signal.aborted) { reason = "cancelled"; break iteration; }
        if (executed >= MAX_EXECUTED_STEPS) { reason = "limit"; break iteration; }
        const result = adapter.execute(step, targetEof);
        executed++; targetEof = result.targetEof;
        if (!result.ok) { reason = result.exhausted ? "failed" : "failed"; break iteration; }
        if (mode.kind === "until-eof" && result.cursor >= targetEof) { reason = "eof"; iterations++; break iteration; }
        if (executed % 250 === 0) { onProgress?.(iterations, executed); await adapter.yieldControl(); }
      }
      if (mode.kind === "until-eof" && adapter.cursor() <= before) { reason = "stalled"; iterations++; break; }
      onProgress?.(iterations + 1, executed);
    }
  } finally { adapter.endHistoryGroup(); }
  return { iterations, steps: executed, reason };
}
